import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TextField,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { useSnackbar } from 'hooks/use-snackbar';
import { getStoredItems } from 'api/picking/get-stored-items';
import { useSearchParams } from 'react-router-dom';
import { LocationSelectionDialog } from 'components/LocationSelectionDialog';
import ProductSelectionDialog from 'components/ProductSelectionDialog';
import { StoredItemVm } from 'api/picking/stored-item.vm';
import { LocationVm } from 'api/warehouse/location.vm';
import { ProductVm } from 'api/catalog/product.vm';

const StoredItemsPage = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const [storedItems, setStoredItems] = useState<StoredItemVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const warehouseId = searchParams.get('warehouseId');
  const [selectedLocation, setSelectedLocation] = useState<LocationVm | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductVm | null>(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  const productId = searchParams.get('productId') || '';
  const locationId = searchParams.get('locationId') || '';

  const fetchStoredItems = async (offset: string | null = null, reset: boolean = false) => {
    if (!warehouseId) return;
    setLoading(true);
    getStoredItems(warehouseId, productId || null, locationId || null, offset || null, {
      success: (response: any) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setStoredItems(reset ? response.objects : [...storedItems, ...response.objects]);
        setLoading(false);
      },
      error: (err: any) => {
        console.error('Falha ao obter itens armazenados:', err);
        snackbar.show('Falha ao carregar itens armazenados', null, 'error');
        setLoading(false);
      },
    });
  };

  const fetchWarehouses = () => {
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        setWarehouses(response.objects);
        if (response.objects.length && !warehouseId) {
          setSearchParams((prev) => {
            prev.set('warehouseId', response.objects[0].id);
            return prev;
          });
        }
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
        snackbar.show('Falha ao carregar armazéns', null, 'error');
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    // React to changes in warehouseId, productId, locationId
    if (warehouseId) {
      fetchStoredItems(null, true);
    } else {
      setStoredItems([]);
    }
    // eslint-disable-next-line
  }, [warehouseId, productId, locationId]);

  const handleClearFilter = () => {
    setSelectedProduct(null);
    setSelectedLocation(null);
    setSearchParams((prev) => {
      prev.delete('productId');
      prev.delete('locationId');
      return prev;
    });
  };

  const handleLocationSelect = (location: LocationVm) => {
    setSelectedLocation(location);
    setSearchParams((prev) => {
      prev.set('locationId', location.id);
      return prev;
    });
    setLocationDialogOpen(false);
  };

  const handleProductSelect = (product: ProductVm) => {
    setSelectedProduct(product);
    setSearchParams((prev) => {
      prev.set('productId', product.id);
      return prev;
    });
    setProductDialogOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Stock
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="warehouse-select-label">Selecionar Armazém</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={warehouseId || ''}
                label="Selecionar Armazém"
                onChange={(e) => {
                  setSearchParams((prev) => {
                    prev.set('warehouseId', e.target.value);
                    return prev;
                  });
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{warehouse.name}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Localização"
              fullWidth
              value={selectedLocation ? selectedLocation.id : ''}
              InputProps={{
                readOnly: true,
              }}
              onClick={() => setLocationDialogOpen(true)}
              placeholder="Selecionar Localização"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Produto"
              fullWidth
              value={selectedProduct ? selectedProduct.name : ''}
              InputProps={{
                readOnly: true,
              }}
              onClick={() => setProductDialogOpen(true)}
              placeholder="Selecionar Produto"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="outlined" color="primary" onClick={handleClearFilter} fullWidth sx={{ height: '56px' }}>
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>Localização</TableCell>
              <TableCell>Produto</TableCell>
              <TableCell>Serial/Lote</TableCell>
              <TableCell>Quantidade</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {storedItems.map((item, idx) => (
              <TableRow key={idx} hover>
                <TableCell>{item.locationId}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.productSku && (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.productSku}
                      </Typography>
                    )}
                    {item.productSku && <span>-</span>}
                    <Typography variant="body2">{item.productName || item.productId}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{item.serialNumber || item.batch || '-'}</TableCell>
                <TableCell>{item.quantity}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={() => fetchStoredItems(nextOffset)} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : storedItems.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais itens para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhum item encontrado
          </Typography>
        )}
      </Box>
      {warehouseId && (
        <LocationSelectionDialog
          open={locationDialogOpen}
          warehouseId={warehouseId || ''}
          onClose={() => setLocationDialogOpen(false)}
          onLocationSelect={handleLocationSelect}
        />
      )}
      <ProductSelectionDialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} onProductSelect={handleProductSelect} />
    </Box>
  );
};

export default StoredItemsPage;
